import { useBreakpoint } from '@/lib/useBreakpoint';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { actionAriaLabel, actionTooltip } from '@/components/rowActionModel';

export interface RowAction {
  key: string;
  label: string;
  icon: string;
  onSelect: () => void;
  destructive?: boolean;
  /** Shown but disabled, with this reason ("Requires admin", "Available in P4"). */
  disabledReason?: string;
  testId?: string;
}

interface Props {
  actions: RowAction[];
  /** The row's name, for accessible labels ("Edit StoreAI"). */
  subject: string;
  /** `text`: ghost buttons with labels · `icon`: icon buttons with tooltips · `menu`: one "more" menu. Phones always get the menu. */
  variant?: 'text' | 'icon' | 'menu';
  className?: string;
  testId?: string;
}

function RowActionsMenu({ actions, subject, className, testId }: Omit<Props, 'variant'>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={testId ? `${testId}-menu` : undefined} aria-label={`Actions for ${subject}`} className={cn('text-subtle', className)}>
          <Icon name="more_vert" size={18} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        {actions.map((a) => (
          <DropdownMenuItem key={a.key} data-testid={a.testId} variant={a.destructive ? 'destructive' : 'default'} disabled={Boolean(a.disabledReason)} onSelect={a.onSelect}>
            <Icon name={a.icon} size={18} />
            <span className="min-w-0 flex-1">{a.label}</span>
            {a.disabledReason ? <span className="text-xs text-subtle">{a.disabledReason}</span> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The actions of a table row or list item (edit, archive…). Inline from 641 px, one "more" menu on phones (44 px
 * items). A disabled action stays visible with its reason: a tooltip inline, a suffix in the menu.
 */
export function RowActions({ actions, subject, variant = 'text', className, testId }: Props) {
  const phone = useBreakpoint() === 'phone';
  if (actions.length === 0) return null;
  if (phone || variant === 'menu') return <RowActionsMenu actions={actions} subject={subject} className={className} testId={testId} />;
  return (
    <span className={cn('inline-flex items-center gap-0.5', className)} data-testid={testId}>
      {actions.map((a) => (
        <ActionButton key={a.key} action={a} subject={subject} iconOnly={variant === 'icon'} />
      ))}
    </span>
  );
}

function actionClass(iconOnly: boolean, destructive: boolean | undefined, blocked: boolean): string {
  return cn(iconOnly ? 'text-subtle' : 'px-2 text-muted-foreground', destructive && !blocked && 'hover:text-destructive');
}

function ActionButton({ action: a, subject, iconOnly }: { action: RowAction; subject: string; iconOnly: boolean }) {
  const blocked = Boolean(a.disabledReason);
  const button = (
    <Button
      variant="ghost"
      size={iconOnly ? 'icon-sm' : 'sm'}
      data-testid={a.testId}
      aria-label={actionAriaLabel(a, subject, iconOnly)}
      aria-disabled={blocked || undefined}
      onClick={blocked ? (e) => e.preventDefault() : a.onSelect}
      className={actionClass(iconOnly, a.destructive, blocked)}
    >
      {iconOnly ? <Icon name={a.icon} size={16} /> : a.label}
    </Button>
  );
  if (!iconOnly && !blocked) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{actionTooltip(a)}</TooltipContent>
    </Tooltip>
  );
}
